// Pago de Clientes · TENEDURÍA DE PAGOS (pedido de Rodrigo, 2026-10-28; como la «Teneduría de Pagos»
// de Random, donde tesorería revisa y arregla los cobros ya hechos sin pasar por la nota de venta):
// todos los recibos —del mesón y de cobranza— con sus medios, sus aplicaciones y su turno de caja, y
// las correcciones con motivo: corregir CON QUÉ se cobró (mismo total), anular un recibo sin aplicar,
// reversar una aplicación y aplicar un saldo a favor. Tesorería pasa por encima del turno cerrado con
// su propio permiso. Todo queda auditado: quién, cuándo, motivo, antes y después.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { api } from '../api/cliente';
import type { MedioCaja } from '../api/contratos';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { Modal } from '../comun/Modal';
import { fechaHora, pesos } from '../comun/formato';
import {
  entero,
  filaNueva,
  medioDe,
  NOMBRE_MEDIO,
  problemaDe,
  type Fila,
  type Terminal,
} from '../comun/medios';
import { useSesion } from '../sesion/SesionContexto';
import { SelectorCliente } from './ConsultasCobranza';
import {
  clavesCobranza,
  detallePago,
  NOMBRE_TIPO_DTE,
  useRecibos,
  type ReciboCobro,
} from './datos';
import { EditorMedios } from './EditorMedios';
import { ImprimirRecibo } from './Impresos';
import { SolicitudesAnulacion } from '../caja/SolicitudesAnulacion';

const MEDIOS: MedioCaja[] = ['EFECTIVO', 'DEBITO', 'CREDITO', 'TRANSFERENCIA', 'CHEQUE'];

/** Lo aplicado neto del recibo (aplicaciones menos reversas): con 0 se puede anular. */
const neto = (r: ReciboCobro) => Number(r.aplicado || 0);
const turnoCerrado = (r: ReciboCobro) => !!r.turno && r.turno.estado !== 'ABIERTO';
const origenDe = (r: ReciboCobro) =>
  r.destino === 'NOTA_VENTA' ? `Mesón · ${r.notaNumero ?? ''}` : 'Cobranza';

/** El medio tal como está guardado, listo para editarlo con las mismas reglas del cobro. */
const filaDe = (m: ReciboCobro['medios'][number]): Fila => ({
  ...filaNueva(m.monto, m.medio as MedioCaja),
  terminal: (m.terminal === 'LINK_PAGO' ? 'LINK_PAGO' : 'GETNET') as Terminal,
  autorizacion: m.autorizacion ?? '',
  cuotas: m.cuotas ? String(m.cuotas) : '',
  banco: m.banco ?? '',
  numero: m.numeroDocumento ?? '',
  fecha: m.fechaDocumento ?? '',
  referencia: m.referencia ?? '',
});

const medioCorto = (m: ReciboCobro['medios'][number]) =>
  [
    NOMBRE_MEDIO[m.medio as MedioCaja] ?? m.medio,
    m.autorizacion ? `aut. ${m.autorizacion}` : '',
    m.cuotas && m.cuotas > 1 ? `${m.cuotas} cuotas` : '',
  ]
    .filter(Boolean)
    .join(' ');

export function TeneduriaPagos({
  alAplicar,
}: {
  /** Abre «Cobro · aplicación» para ese cliente (aplicar el saldo a favor de un recibo). */
  alAplicar?: ((c: { id: string; razonSocial: string }) => void) | undefined;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const intentos = useIntentos();
  const [cliente, setCliente] = useState<{ id: string; razonSocial: string } | null>(null);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [texto, setTexto] = useState('');
  const [textoBuscado, setTextoBuscado] = useState('');
  const [medio, setMedio] = useState('');
  const [estado, setEstado] = useState<'' | 'VIGENTE' | 'ANULADO'>('');
  const [soloConSaldo, setSoloConSaldo] = useState(false);
  const [abierto, setAbierto] = useState<ReciboCobro | null>(null);
  const [imprimir, setImprimir] = useState<ReciboCobro | null>(null);
  const terminar = useCallback(() => setImprimir(null), []);
  const [reversar, setReversar] = useState<{ aplicacionId: string; texto: string } | null>(null);
  const [anular, setAnular] = useState<ReciboCobro | null>(null);
  const [corregir, setCorregir] = useState<ReciboCobro | null>(null);
  const [filas, setFilas] = useState<Fila[]>([]);
  const [motivo, setMotivo] = useState('');
  const q = useRecibos(empresaId, {
    clienteEntidadId: cliente?.id,
    ...(desde ? { desde } : {}),
    ...(hasta ? { hasta } : {}),
    ...(textoBuscado ? { texto: textoBuscado } : {}),
    ...(medio ? { medio } : {}),
    ...(estado ? { estado } : {}),
    soloConSaldo,
  });
  const deTesoreria = (a: 'anular' | 'corregir') => puede(`tesoreria.recibo.${a}`);
  const puedeCorregir = puede('caja.recibo.corregir') || deTesoreria('corregir');
  const puedeAnular = puede('caja.recibo.anular') || deTesoreria('anular');
  const cerrar = () => {
    setReversar(null);
    setAnular(null);
    setCorregir(null);
    setAbierto(null);
    setMotivo('');
  };
  const refrescar = async () => {
    cerrar();
    await qc.invalidateQueries({ queryKey: clavesCobranza.cobranza(empresaId) });
  };
  const reversa = useMutation({
    mutationFn: () =>
      api.post(`/tesoreria/aplicaciones/${reversar!.aplicacionId}/reversar`, {
        motivo: motivo.trim(),
      }),
    onSuccess: refrescar,
  });
  const anulacion = useMutation({
    mutationFn: () => {
      const cuerpo = { motivo: motivo.trim(), version: anular!.version };
      return api.post(`/caja/recibos/${anular!.reciboId}/anular`, cuerpo, {
        idempotencia: intentos.clave('anular-recibo', { id: anular!.reciboId, ...cuerpo }),
      });
    },
    onSuccess: refrescar,
  });
  const correccion = useMutation({
    mutationFn: () => {
      const cuerpo = {
        medios: filas.map(medioDe),
        motivo: motivo.trim(),
        version: corregir!.version,
      };
      return api.post(`/caja/recibos/${corregir!.reciboId}/medios`, cuerpo, {
        idempotencia: intentos.clave('corregir-medios', { id: corregir!.reciboId, ...cuerpo }),
      });
    },
    onSuccess: refrescar,
  });
  const abrirCorreccion = (r: ReciboCobro) => {
    setFilas(r.medios.map(filaDe));
    setMotivo('');
    setCorregir(r);
  };
  const totalFilas = filas.reduce((s, f) => s + (entero(f.monto) || 0), 0);
  const problemas = filas.map(problemaDe).filter(Boolean);
  const correccionLista =
    !!corregir &&
    filas.length > 0 &&
    problemas.length === 0 &&
    totalFilas === Number(corregir.total) &&
    motivo.trim().length >= 3;

  const columnas: Columna<ReciboCobro>[] = [
    {
      id: 'n',
      titulo: 'Recibo',
      celda: (r) => <span className="mono">{r.numeroVisible}</span>,
      ancho: '8rem',
    },
    { id: 'fecha', titulo: 'Fecha', celda: (r) => fechaHora(r.creadoEn), ancho: '9.5rem' },
    {
      id: 'cliente',
      titulo: 'Cliente',
      celda: (r) => r.clienteRazonSocial ?? '',
    },
    { id: 'origen', titulo: 'Origen', celda: origenDe, ancho: '9rem' },
    {
      id: 'medios',
      titulo: 'Medios',
      celda: (r) => r.medios.map(medioCorto).join(' + '),
    },
    { id: 'total', titulo: 'Total', celda: (r) => pesos(r.total), alinear: 'der', ancho: '7rem' },
    {
      id: 'aplicado',
      titulo: 'Aplicado',
      celda: (r) => pesos(r.aplicado),
      alinear: 'der',
      ancho: '7rem',
    },
    {
      id: 'favor',
      titulo: 'A favor',
      celda: (r) => (Number(r.saldoAFavor) ? pesos(r.saldoAFavor) : ''),
      alinear: 'der',
      ancho: '6.5rem',
    },
    {
      id: 'caja',
      titulo: 'Caja',
      celda: (r) =>
        r.turno ? (
          <span title={`${r.turno.punto} · turno ${r.turno.numero}`}>
            {r.turno.numero}
            {turnoCerrado(r) ? <span className="apagado"> (cerrado)</span> : ''}
          </span>
        ) : (
          <span className="apagado">sin turno</span>
        ),
      ancho: '8rem',
    },
    {
      id: 'quien',
      titulo: 'Quién',
      celda: (r) => r.creadoPorNombre ?? '',
      ancho: '8rem',
    },
    {
      id: 'estado',
      titulo: 'Estado',
      celda: (r) =>
        r.estado === 'VIGENTE' ? (
          neto(r) > 0 ? (
            <span className="insignia tono-ok">aplicado</span>
          ) : (
            <span className="insignia tono-info">vigente</span>
          )
        ) : (
          <span className="insignia tono-malo" title={r.motivoAnulacion ?? ''}>
            anulado
          </span>
        ),
      ancho: '6rem',
    },
    {
      id: 'imprimir',
      titulo: '',
      celda: (r) => (
        <button
          type="button"
          className="boton-mini"
          aria-label={`Imprimir comprobante ${r.numeroVisible}`}
          onClick={(e) => {
            e.stopPropagation();
            setImprimir(r);
          }}
        >
          Imprimir
        </button>
      ),
      ancho: '6rem',
    },
  ];

  return (
    <div className="pantalla consulta" data-pantalla="historial-cobros">
      {/* 0130 V4: las anulaciones que pidió el turno (tesorería las ejecuta o rechaza). */}
      {puedeAnular && (
        <SolicitudesAnulacion
          empresaId={empresaId}
          alCambiar={() => qc.invalidateQueries({ queryKey: clavesCobranza.cobranza(empresaId) })}
        />
      )}
      <div className="barra-filtros">
        <SelectorCliente cliente={cliente} alElegir={setCliente} />
        <label className="campo">
          <span className="etiqueta">N° recibo o nota</span>
          <input
            aria-label="N° de recibo o de nota"
            placeholder="235 · NV-000254"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setTextoBuscado(texto.trim());
            }}
            onBlur={() => setTextoBuscado(texto.trim())}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Desde</span>
          <input
            type="date"
            aria-label="Desde"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Hasta</span>
          <input
            type="date"
            aria-label="Hasta"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Medio</span>
          <select aria-label="Medio" value={medio} onChange={(e) => setMedio(e.target.value)}>
            <option value="">Todos</option>
            {MEDIOS.map((m) => (
              <option key={m} value={m}>
                {NOMBRE_MEDIO[m]}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Estado</span>
          <select
            aria-label="Estado"
            value={estado}
            onChange={(e) => setEstado(e.target.value as '' | 'VIGENTE' | 'ANULADO')}
          >
            <option value="">Todos</option>
            <option value="VIGENTE">Vigentes</option>
            <option value="ANULADO">Anulados</option>
          </select>
        </label>
        <label className="campo-check">
          <input
            type="checkbox"
            checked={soloConSaldo}
            onChange={(e) => setSoloConSaldo(e.target.checked)}
          />{' '}
          Solo con saldo a favor (anticipos)
        </label>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Recibos"
        columnas={columnas}
        filas={q.data ?? []}
        clave={(r) => r.reciboId}
        cargando={q.isLoading}
        vacio="Sin recibos con esos filtros"
        alAbrir={setAbierto}
      />

      {abierto && !corregir && !anular && !reversar && (
        <Modal
          titulo={`Recibo ${abierto.numeroVisible} · ${abierto.clienteRazonSocial ?? ''}`}
          alCerrar={() => setAbierto(null)}
          ancho="ancho"
        >
          <dl className="datos-ficha" aria-label="Datos del recibo">
            <dt>Fecha</dt>
            <dd>{fechaHora(abierto.creadoEn)}</dd>
            <dt>Origen</dt>
            <dd>{origenDe(abierto)}</dd>
            <dt>Caja</dt>
            <dd>
              {abierto.turno
                ? `${abierto.turno.punto} · ${abierto.turno.numero}${turnoCerrado(abierto) ? ' (turno cerrado)' : ' (turno abierto)'}`
                : 'Sin turno (solo transferencia)'}
            </dd>
            <dt>Quién</dt>
            <dd>{abierto.creadoPorNombre ?? ''}</dd>
            {abierto.observacion && (
              <>
                <dt>Observación</dt>
                <dd>{abierto.observacion}</dd>
              </>
            )}
            <dt>Estado</dt>
            <dd>
              {abierto.estado === 'VIGENTE'
                ? `Vigente · aplicado ${pesos(abierto.aplicado)} · a favor ${pesos(abierto.saldoAFavor)}`
                : `Anulado${abierto.anuladoEn ? ` el ${fechaHora(abierto.anuladoEn)}` : ''}: ${abierto.motivoAnulacion ?? ''}`}
            </dd>
          </dl>

          <h3>Con qué se cobró</h3>
          <table className="grilla compacta" aria-label="Medios del recibo">
            <thead>
              <tr>
                <th>Medio</th>
                <th>Detalle</th>
                <th className="der">Monto</th>
              </tr>
            </thead>
            <tbody>
              {abierto.medios.map((m) => (
                <tr key={m.linea ?? `${m.medio}-${m.monto}`}>
                  <td>{NOMBRE_MEDIO[m.medio as MedioCaja] ?? m.medio}</td>
                  <td>
                    {[
                      detallePago(m).replace(NOMBRE_MEDIO[m.medio as MedioCaja] ?? m.medio, ''),
                      m.autorizacion ? `aut. ${m.autorizacion}` : '',
                      m.cuotas && m.cuotas > 1 ? `${m.cuotas} cuotas` : '',
                      m.fechaDocumento ?? '',
                    ]
                      .map((x) => x.replace(/^\s*·\s*/, '').trim())
                      .filter(Boolean)
                      .join(' · ')}
                  </td>
                  <td className="der">{pesos(m.monto)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Aplicaciones</h3>
          {abierto.aplicaciones.length === 0 ? (
            <p className="apagado">Sin aplicaciones: todo el recibo está a favor del cliente.</p>
          ) : (
            <table className="grilla compacta" aria-label="Aplicaciones del recibo">
              <thead>
                <tr>
                  <th>Documento</th>
                  <th>Tipo</th>
                  <th>Pagado con</th>
                  <th className="der">Monto</th>
                  <th>Cuándo</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {abierto.aplicaciones.map((a) => (
                  <tr
                    key={a.aplicacionId}
                    className={a.reversada || a.tipo === 'REVERSA' ? 'apagado' : ''}
                  >
                    <td>
                      {NOMBRE_TIPO_DTE[a.tipoDte ?? 0] ?? 'Documento'} N° {a.folio ?? '—'}
                    </td>
                    <td>
                      {a.tipo === 'REVERSA'
                        ? 'Reversa'
                        : a.origen === 'AUTOMATICA'
                          ? 'Automática'
                          : 'Manual'}
                    </td>
                    <td>
                      {(a.medios ?? [])
                        .map((x) => {
                          const m = abierto.medios.find((y) => y.linea === x.linea) ?? {
                            medio: x.medio,
                          };
                          return `${detallePago(m)} ${pesos(x.monto)}`;
                        })
                        .join(' + ')}
                    </td>
                    <td className="der">{pesos(a.monto)}</td>
                    <td>{fechaHora(a.aplicadoEn)}</td>
                    <td>
                      {a.tipo === 'APLICACION' &&
                        !a.reversada &&
                        puede('cobranza.aplicacion.reversar') && (
                          <button
                            type="button"
                            className="boton-mini"
                            onClick={() => {
                              setMotivo('');
                              setReversar({
                                aplicacionId: a.aplicacionId,
                                texto: `${a.folio ?? ''} · ${pesos(a.monto)}`,
                              });
                            }}
                          >
                            Reversar
                          </button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {abierto.estado === 'VIGENTE' && (
            <>
              {neto(abierto) > 0 && puedeAnular && (
                <p className="apagado">Para anular el recibo, primero reversa sus aplicaciones.</p>
              )}
              {turnoCerrado(abierto) &&
                !deTesoreria('anular') &&
                !deTesoreria('corregir') &&
                (puedeAnular || puedeCorregir) && (
                  <p className="apagado">
                    El turno de caja de este recibo ya cerró: lo corrige tesorería.
                  </p>
                )}
            </>
          )}
          <div className="acciones-modal">
            <button type="button" onClick={() => setImprimir(abierto)}>
              Imprimir
            </button>
            {abierto.estado === 'VIGENTE' &&
              Number(abierto.saldoAFavor) > 0 &&
              alAplicar &&
              puede('cobranza.cobro.registrar') && (
                <button
                  type="button"
                  onClick={() =>
                    alAplicar({
                      id: abierto.clienteEntidadId,
                      razonSocial: abierto.clienteRazonSocial ?? '',
                    })
                  }
                >
                  Aplicar saldo a favor
                </button>
              )}
            {abierto.estado === 'VIGENTE' &&
              puedeCorregir &&
              (!turnoCerrado(abierto) || deTesoreria('corregir')) && (
                <button type="button" onClick={() => abrirCorreccion(abierto)}>
                  Corregir medios…
                </button>
              )}
            {abierto.estado === 'VIGENTE' &&
              puedeAnular &&
              neto(abierto) === 0 &&
              (!turnoCerrado(abierto) || deTesoreria('anular')) && (
                <button
                  type="button"
                  className="peligro"
                  onClick={() => {
                    setMotivo('');
                    setAnular(abierto);
                  }}
                >
                  Anular recibo…
                </button>
              )}
          </div>
        </Modal>
      )}

      {corregir && (
        <Modal
          titulo={`Corregir medios · ${corregir.numeroVisible} · ${pesos(corregir.total)}`}
          alCerrar={() => setCorregir(null)}
          ancho="ancho"
        >
          <p>
            Cambia CON QUÉ se cobró (por ejemplo, se marcó efectivo y el cliente pagó con tarjeta).
            Como en Random, este recibo queda anulado y sale uno nuevo, con el número siguiente y el
            mismo total; las facturas o boletas pagadas con él siguen pagadas, ahora desde el nuevo
            y con los medios correctos. Queda registrado quién, cuándo, por qué y cómo estaba.
          </p>
          <EditorMedios filas={filas} alCambiar={setFilas} sugerido={Number(corregir.total)} />
          {totalFilas !== Number(corregir.total) && filas.length > 0 && (
            <p className="tono-malo">
              Los medios suman {pesos(String(totalFilas))} y el recibo es de {pesos(corregir.total)}
              : deben ser iguales.
            </p>
          )}
          <label className="campo">
            <span className="etiqueta">Motivo</span>
            <input
              aria-label="Motivo de la corrección"
              placeholder="se registró en efectivo y el cliente pagó con tarjeta"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          {correccion.isError && <PanelError error={correccion.error} />}
          <div className="acciones-modal">
            <button type="button" onClick={() => setCorregir(null)}>
              Volver
            </button>
            <button
              type="button"
              className="primario"
              disabled={!correccionLista || correccion.isPending}
              onClick={() => correccion.mutate()}
            >
              Guardar corrección
            </button>
          </div>
        </Modal>
      )}

      {anular && (
        <Modal
          titulo={`Anular recibo ${anular.numeroVisible} · ${pesos(anular.total)}`}
          alCerrar={() => setAnular(null)}
        >
          <p>
            El recibo deja de contar como pago: la nota o el documento vuelven a deberlo y el dinero
            sale del arqueo del turno
            {turnoCerrado(anular)
              ? ' (el turno ya cerró: su arqueo sellado no se recalcula; queda dicho en la auditoría)'
              : ''}
            . Queda registrado quién y por qué.
          </p>
          <label className="campo">
            <span className="etiqueta">Motivo</span>
            <input
              aria-label="Motivo de la anulación"
              autoFocus
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          {anulacion.isError && <PanelError error={anulacion.error} />}
          <div className="acciones-modal">
            <button type="button" onClick={() => setAnular(null)}>
              Volver
            </button>
            <button
              type="button"
              className="peligro"
              disabled={motivo.trim().length < 3 || anulacion.isPending}
              onClick={() => anulacion.mutate()}
            >
              Anular
            </button>
          </div>
        </Modal>
      )}

      {reversar && (
        <Modal titulo={`Reversar aplicación ${reversar.texto}`} alCerrar={() => setReversar(null)}>
          <p>
            El monto vuelve al saldo a favor del recibo y el documento vuelve a deberlo. Queda
            registrado quién y por qué.
          </p>
          <label className="campo">
            <span className="etiqueta">Motivo</span>
            <input
              aria-label="Motivo de la reversa"
              autoFocus
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          {reversa.isError && <PanelError error={reversa.error} />}
          <div className="acciones-modal">
            <button type="button" onClick={() => setReversar(null)}>
              Volver
            </button>
            <button
              type="button"
              className="primario"
              disabled={motivo.trim().length < 5 || reversa.isPending}
              onClick={() => reversa.mutate()}
            >
              Reversar
            </button>
          </div>
        </Modal>
      )}

      {imprimir && (
        <ImprimirRecibo
          reciboId={imprimir.reciboId}
          clienteId={imprimir.clienteEntidadId}
          nombre={imprimir.clienteRazonSocial ?? cliente?.razonSocial ?? ''}
          alTerminar={terminar}
        />
      )}
    </div>
  );
}
